import { useCallback } from 'react';

import throttle from 'lodash/throttle';
import { c } from 'ttag';

import { useReferrals } from '@proton/account/referrals/hooks';
import { useUserSettings } from '@proton/account/userSettings/hooks';
import { useConfig } from '@proton/app-context/useConfig';
import { useNotifications } from '@proton/app-context/useNotifications';
import { Button } from '@proton/atoms/Button/Button';
import { ButtonLike } from '@proton/atoms/Button/ButtonLike';
import { IcSquares } from '@proton/icons/icons/IcSquares';
import { textToClipboard } from '@proton/shared/lib/helpers/browser';

import DrawerAppHeadline from '../../../components/drawer/views/shared/DrawerAppHeadline';
import DrawerAppSection from '../../../components/drawer/views/shared/DrawerAppSection';
import SettingsLink from '../../../components/link/SettingsLink';
import Loader from '../../../components/loader/Loader';
import { useReferralTelemetry } from '../hooks/useReferralTelemetry';

const InviteShareLink = () => {
    const [referral, loadingReferral] = useReferrals();

    const [userSettings] = useUserSettings();
    const { createNotification } = useNotifications();
    const { APP_NAME: currentApp } = useConfig();
    const { sendCopyLinkDrawer } = useReferralTelemetry();

    // TODO: referral: Add this to referralInfo redux state. Along with formatting. And use in the other InviteShareLink too
    const referrerLink = userSettings.Referral?.Link || '';

    const referrerLinkTrimmed = referrerLink.replace('https://', '');

    const onCopyButtonClick = useCallback(
        throttle(
            () => {
                sendCopyLinkDrawer();
                textToClipboard(referrerLink);
                createNotification({
                    text: c('Info').t`Referral link copied to your clipboard`,
                });
            },
            1500,
            { leading: true, trailing: false }
        ),
        [referrerLink, sendCopyLinkDrawer]
    );

    if (loadingReferral) {
        return <Loader />;
    }

    if (referral.status.hasReachedRewardLimit) {
        return null;
    }

    return (
        <>
            <DrawerAppHeadline>{c('Title').t`Share your link`}</DrawerAppHeadline>
            <DrawerAppSection>
                <div className="flex justify-space-between flex-nowrap gap-2 flex-row w-full">
                    <div className="flex-auto flex items-center user-select" title={referrerLink}>
                        <span className="text-ellipsis text-lg">{referrerLinkTrimmed}</span>
                    </div>
                    <Button
                        color="norm"
                        shape="ghost"
                        onClick={onCopyButtonClick}
                        title={c('Info').t`Copy your referral link`}
                        disabled={!referrerLink}
                        noDisabledStyles
                        icon
                        className="shrink-0"
                        size="small"
                    >
                        <IcSquares className="shrink-0" alt={c('Info').t`Copy your referral link`} />
                    </Button>
                </div>
            </DrawerAppSection>

            <ButtonLike
                as={SettingsLink}
                target="_blank"
                path="/referral"
                app={currentApp}
                color="norm"
                shape="underline"
                size="small"
                className="text-sm"
            >{c('Referral').t`More sharing options`}</ButtonLike>
        </>
    );
};

export default InviteShareLink;
